import { useTheme } from '@shopify/restyle';
import { Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';

import { useNavigation } from 'app/hooks/navigation';
import { localeAwareFormat } from 'app/lib/date';
import { getListingTransactionMode } from 'app/lib/listing';
import { formatCurrency } from 'app/localization/i18n';
import { useUser, useUserCurrency } from 'app/network/user';
import { navigateToLoan } from 'app/screens/root-stack-helper';
import type { Loan } from 'app/types/loan';
import { TransactionMode } from 'app/types/listing';
import { LoanType, canUserRateLoan, getCurrentUserLoanRating, getLoanType } from 'app/types/loan';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, View } from 'react-native';
import { ClubName } from './club-name';
import { ListingThumbnail } from './listing-thumbnail';
import type { Theme } from 'app/shopify';
import { IconChevronRight } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { getTimelineLoanCardDate } from './timeline-loan-card.utils';

const TIMELINE_WIDTH = 52;
const DATE_BADGE_SIZE = 36;
const LINE_WIDTH = 2;

function TimelineMarker({
  badgeTop,
  dayNum,
  isFirst,
  isLast,
  isNewMonth,
  monthAbbr,
  monthYearLabel,
  children,
}: {
  badgeTop: number;
  dayNum: string;
  isFirst: boolean;
  isLast: boolean;
  isNewMonth: boolean;
  monthAbbr: string;
  monthYearLabel: string;
  children: ReactNode;
}) {
  const theme = useTheme<Theme>();
  const timeLineColor = theme.colors['text.brand'];
  return (
    <Column>
      {isNewMonth ? (
        <Row
          alignItems="center"
          marginBottom="spacing.general.x2"
          marginTop={isFirst ? 'spacing.general.0' : 'spacing.general.x2'}
        >
          <View style={{ width: TIMELINE_WIDTH, alignItems: 'center' }}>
            <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: timeLineColor }} />
          </View>
          <Text color="text.primary" fontSize={13} fontWeight="700" letterSpacing={0.8} textTransform="uppercase">
            {monthYearLabel}
          </Text>
        </Row>
      ) : null}
      <Row style={{ minHeight: 80 }}>
        <View style={{ width: TIMELINE_WIDTH, alignItems: 'center' }}>
          {!isFirst || isNewMonth ? (
            <View
              style={{
                position: 'absolute',
                top: 0,
                width: LINE_WIDTH,
                height: badgeTop,
                backgroundColor: theme.colors['surface.disabled'],
              }}
            />
          ) : null}
          <View
            style={{
              position: 'absolute',
              top: badgeTop,
              width: DATE_BADGE_SIZE,
              height: DATE_BADGE_SIZE,
              borderRadius: DATE_BADGE_SIZE / 2,
              backgroundColor: theme.colors.white,
              borderWidth: 2,
              borderColor: timeLineColor,
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
            }}
          >
            <Text fontSize={14} fontWeight="800" lineHeight={16} style={{ color: timeLineColor }}>
              {dayNum}
            </Text>
            <Text fontSize={8} fontWeight="600" lineHeight={10} style={{ color: timeLineColor }}>
              {monthAbbr}
            </Text>
          </View>
          {!isLast ? (
            <View
              style={{
                position: 'absolute',
                top: badgeTop + DATE_BADGE_SIZE,
                bottom: 0,
                width: LINE_WIDTH,
                backgroundColor: theme.colors['surface.disabled'],
              }}
            />
          ) : null}
        </View>
        {children}
      </Row>
    </Column>
  );
}

function TimelineLoanPrice({ loan, userCurrency }: { loan: Loan; userCurrency: Parameters<typeof formatCurrency>[1] }) {
  const theme = useTheme<Theme>();
  const displayPrice =
    getListingTransactionMode(loan.listing) === TransactionMode.sell
      ? loan.listing.price
      : loan.listing.estimatedPrice !== null && loan.listing.estimatedPrice !== undefined
        ? `${String(loan.listing.estimatedPrice)} $`
        : null;
  if (!displayPrice) return null;
  const numericMatch = /-?\d+(\.\d+)?/.exec(displayPrice);
  const content = numericMatch ? formatCurrency(parseFloat(numericMatch[0]), userCurrency, 0) : displayPrice;
  return (
    <Row
      style={{
        paddingHorizontal: theme.spacing['spacing.general.x1'],
        paddingVertical: 2,
        backgroundColor: theme.colors['surface.tertiary-neutral'],
        borderRadius: theme.borderRadii['radius.sm'],
        position: 'absolute',
        top: theme.spacing['spacing.general.x3'],
        right: theme.spacing['spacing.general.x3'],
      }}
    >
      <Text color="text.positive" fontSize={11}>
        {content}
      </Text>
    </Row>
  );
}

function TimelineRatingActions({
  canRate,
  hasRated,
  isRecommended,
  onOpenMyRating,
  onOpenRating,
}: {
  canRate: boolean;
  hasRated: boolean;
  isRecommended: boolean;
  onOpenMyRating: () => void;
  onOpenRating: (initialExperience: 'positive' | 'negative') => void;
}) {
  const { t } = useTranslation('loans');
  const theme = useTheme<Theme>();
  if (hasRated) {
    return (
      <Pressable
        onPress={onOpenMyRating}
        style={{
          alignItems: 'center',
          borderColor: theme.colors['surface.disabled'],
          borderRadius: 24,
          borderWidth: 1,
          flexDirection: 'row',
          gap: 8,
          justifyContent: 'center',
          marginTop: theme.spacing['spacing.general.x2'],
          padding: 16,
        }}
      >
        <Text fontSize={14} lineHeight={16.5} style={{ color: '#1F2635' }}>
          {t('past.rating.mine')}
        </Text>
        <Text fontSize={14} lineHeight={16.5} style={{ color: '#1F2635' }}>
          {isRecommended ? '👍 ' : '👎 '}
          <Text
            fontSize={14}
            fontWeight="700"
            lineHeight={13.5}
            style={{ color: isRecommended ? theme.colors['text.emphasized'] : theme.colors['text.error'] }}
          >
            {t(isRecommended ? 'past.rating.recommended' : 'past.rating.notRecommended')}
          </Text>
        </Text>
        <IconChevronRight color="#1F2635" size={24} strokeWidth={ICON_STROKE_WIDTH} />
      </Pressable>
    );
  }
  if (!canRate) return null;
  return (
    <Row gap="spacing.general.x2" marginTop="spacing.general.x2">
      {(['positive', 'negative'] as const).map((experience) => (
        <Pressable
          key={experience}
          onPress={() => onOpenRating(experience)}
          style={{
            alignItems: 'center',
            borderColor: '#DDDACF',
            borderRadius: 39,
            borderWidth: 1,
            flex: 1,
            height: 48,
            justifyContent: 'center',
            paddingHorizontal: 14,
          }}
        >
          <Text fontSize={24} fontWeight="700">
            {experience === 'positive' ? '👍' : '👎'}
          </Text>
        </Pressable>
      ))}
    </Row>
  );
}

export function TimelineLoanCard({
  loan,
  isFirst,
  isLast,
  isNewMonth,
}: {
  loan: Loan;
  isFirst: boolean;
  isLast: boolean;
  isNewMonth: boolean;
}) {
  const { t } = useTranslation('loans');
  const theme = useTheme<Theme>();
  const navigation = useNavigation();
  const userQuery = useUser();
  const userCurrency = useUserCurrency();
  const currentUserId = userQuery.data?.id;

  const loanType = getLoanType(loan.borrower, userQuery.data);

  const otherUser = (() => {
    const type = getLoanType(loan.borrower, userQuery.data);
    return type === LoanType.Borrowing ? loan.lender : loan.borrower;
  })();
  const currentUserRating = getCurrentUserLoanRating(loan, currentUserId);
  const hasRated = typeof currentUserRating === 'number';
  const canRate = canUserRateLoan(loan, currentUserId);
  const isRecommended = typeof currentUserRating === 'number' && currentUserRating >= 3;
  const otherUserName = otherUser.profile
    ? `${otherUser.profile.firstName} ${otherUser.profile.lastName}`.trim()
    : t('deletedUser', { ns: 'common' });
  const subjectImage =
    loanType === LoanType.Lending
      ? (otherUser.profile?.picture ?? null)
      : (loan.listing.picture ?? loan.listing.pictures?.[0]?.url ?? null);
  const subjectName = loanType === LoanType.Lending ? otherUserName : loan.listing.item.name;

  const loanDate = getTimelineLoanCardDate(loan.returned, loan.end);
  const dayNum = localeAwareFormat(loanDate, 'd');
  const monthAbbr = localeAwareFormat(loanDate, 'MMM').toUpperCase().replace('.', '');
  const monthYearLabel = localeAwareFormat(loanDate, t('dateFormat_MMMYYYY', { ns: 'common' }));

  const [cardHeight, setCardHeight] = useState(0);
  const badgeTop = cardHeight > 0 ? (cardHeight - DATE_BADGE_SIZE) / 2 : 0;

  const openRating = (initialExperience: 'positive' | 'negative') => {
    navigation.navigate(
      'new-rating',
      {
        loanId: loan.id,
        listingId: loan.listingId,
        channelUrl: loan.channelURL,
        isLender: loanType === LoanType.Lending,
        subjectImage,
        subjectName,
        initialExperience,
      },
      { pop: true },
    );
  };

  const openMyRating = () => {
    navigation.navigate(
      'loan',
      {
        screen: 'ratings',
        params: {
          userId: otherUser.id,
          subjectName: otherUserName,
          scrollToLoanId: loan.id,
        },
      },
      { pop: true },
    );
  };

  const numberOfGroups = otherUser.memberships?.map((m) => m.group).length ?? 0;
  return (
    <TimelineMarker
      badgeTop={badgeTop}
      dayNum={dayNum}
      isFirst={isFirst}
      isLast={isLast}
      isNewMonth={isNewMonth}
      monthAbbr={monthAbbr}
      monthYearLabel={monthYearLabel}
    >
      <Pressable
        onLayout={(e) => setCardHeight(e.nativeEvent.layout.height)}
        onPress={() => navigateToLoan(navigation, loan.id, loan.listing.id)}
        style={{
          flex: 1,
          marginBottom: theme.spacing['spacing.general.x4'],
          padding: theme.spacing['spacing.general.x3'],
          borderRadius: theme.borderRadii['radius.md'],
          boxShadow: `0px 2px 3px ${theme.colors['text.secondary']}26`,
          borderColor: theme.colors['surface.disabled'],
          backgroundColor: theme.colors.white,
        }}
      >
        {/* Other user */}
        <Row alignItems="center" gap="spacing.general.x2" marginBottom="spacing.general.x2">
          <TimelineLoanPrice loan={loan} userCurrency={userCurrency} />
          {otherUser.profile ? (
            <PCImage
              aspectRatio={1}
              style={{ borderRadius: theme.borderRadii['radius.xl'] }}
              height={34}
              source={{ uri: otherUser.profile.picture }}
            />
          ) : (
            <View
              style={{
                width: 34,
                height: 34,
                borderRadius: theme.borderRadii['radius.xl'],
                backgroundColor: theme.colors['surface.tertiary-neutral'],
              }}
            />
          )}
          <Column gap="spacing.general.x1">
            <Text fontSize={14} fontWeight="700" numberOfLines={1}>
              {otherUserName}
            </Text>
            {numberOfGroups > 0 ? (
              <ClubName groups={otherUser.memberships?.map((m) => m.group)} smaller preventNavigateToClubMembers />
            ) : null}
          </Column>
        </Row>

        {/* Listing */}
        <Row
          alignItems="center"
          gap="spacing.general.x2"
          style={{
            backgroundColor: theme.colors['surface.tertiary-neutral'],
            borderRadius: theme.borderRadii['radius.sm'],
            padding: theme.spacing['spacing.general.x1'],
          }}
        >
          <ListingThumbnail hideCorpoBadge size={32} {...loan.listing} isFound={!!loan.listing.lender} />
          <Text fontWeight="600" fontSize={13} numberOfLines={1} flex={1}>
            {loan.listing.item.name}
          </Text>
        </Row>

        <TimelineRatingActions
          canRate={canRate}
          hasRated={hasRated}
          isRecommended={isRecommended}
          onOpenMyRating={openMyRating}
          onOpenRating={openRating}
        />
      </Pressable>
    </TimelineMarker>
  );
}

export { isTimelineLoanCardNewMonth } from './timeline-loan-card.utils';
